import { Form, Input, Modal, Spin, UploadFile, Col, Row } from 'antd';
import { Step, UploadBusinessType } from '../../../../types';
import { useEffect, useState } from 'react';
import { MultiImageUpload } from '../../../../components/multiImageUpload';
import RichEditor from '../../../../components/richEditor';
import SignsField from '../../../../components/signsField';
import {
  entityToUploadFileList,
  uploadFileListToEntityFiles,
} from '../../../../utils/entityUploadFiles';
import styled from 'styled-components';
import { parseSignNames } from '../../../../constants/signs';
import { useTranslation } from 'react-i18next';

export interface AddStepProps {
  open: boolean;
  onConfirm: (step: Step) => void;
  onClose: () => void;
  step?: Step;
}

const ImageCol = styled(Col)`
  border-right: 1px solid #f0f0f0;
  padding-right: 24px;
`;

const FieldsCol = styled(Col)`
  padding-left: 24px;
`;

const AddStep = ({ open, onClose, onConfirm, step }: AddStepProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [description, setDescription] = useState<string>('');

  const handleUploadChange = (list: UploadFile[]) => {
    setUploadFileList(list);
  };

  const handleConfirm = async () => {
    if (isUploading) {
      return;
    }
    try {
      setLoading(true);
      await form.validateFields();
      const formValues = form.getFieldsValue();
      const filePayload = uploadFileListToEntityFiles(uploadFileList, step);
      onConfirm({
        ...formValues,
        description,
        ...filePayload,
      });
      setUploadFileList([]);
      setIsUploading(false);
      setDescription('');
      form.resetFields();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step) {
      form.setFieldsValue({ ...step, signNames: step.signNames || '' });
      setDescription(step.description || '');
    } else {
      form.resetFields();
      form.setFieldsValue({ signNames: '' });
      setDescription('');
    }
    setUploadFileList(entityToUploadFileList(step));
    setIsUploading(false);
  }, [step]);

  return (
    <Modal
      open={open}
      title={t('add_new_step')}
      onOk={handleConfirm}
      onClose={onClose}
      onCancel={onClose}
      okButtonProps={{ disabled: isUploading }}
      width={680}
    >
      <Form form={form} style={{ padding: '24px 0' }} layout="vertical">
        <Spin fullscreen spinning={loading} />
        <Row gutter={24}>
          <ImageCol span={8}>
            <Form.Item label="Image" name="image" style={{ marginBottom: 0 }}>
              <MultiImageUpload
                businessType={UploadBusinessType.ROUTINESTEP}
                value={uploadFileList}
                onChange={handleUploadChange}
                onUploadingChange={setIsUploading}
                multiple={false}
              />
            </Form.Item>
          </ImageCol>
          <FieldsCol span={16}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: 'Please input Step Name!',
                },
              ]}
            >
              <Input type="text" placeholder="Enter" />
            </Form.Item>
            <Form.Item label="Description">
              <RichEditor
                value={description}
                onChange={setDescription}
                placeholder="Enter step description..."
                height={150}
              />
            </Form.Item>
            <Form.Item
              label={
                <span>
                  Safety Instructions (
                  {parseSignNames(step?.signNames || '').length})
                </span>
              }
              name="signNames"
            >
              <SignsField />
            </Form.Item>
          </FieldsCol>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddStep;
